import React, { memo, useEffect } from "react";
import { Swipe, User } from "../../types";
import {
  Card,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Typography
} from '@material-ui/core';
import { actions } from '../../services/userSwipes';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { useQueryParams, StringParam, withDefault } from "use-query-params";
import { areEqual, FixedSizeList, ListChildComponentProps } from "react-window";
import { Box } from "@material-ui/system";
import { ProfileAvatar } from "../../components/UserPhoto/UserPhoto";
import timeAgo from "../../utils/timeAgo";
import { ReactWindowScroller } from "../../components/WindowScroller";

const SwipeItem: React.FC<ListChildComponentProps<Swipe[]>> = ({ data, index, style }) => {

  const swipe = data[ index ];

  return (
    <Box style={style} sx={{ pb: 1 }}>
      <Stack direction="row" justifyContent="center">
        <Card variant="outlined" sx={{ padding: 1, width: '100%', maxWidth: 800, }}>
          <Stack direction="column" spacing={0.2} alignItems="center">
            <Stack direction="row" spacing={1} justifyContent="space-around" alignItems="center" sx={{ width: '100%' }}>
              <ProfileAvatar userId={swipe.userId} size={100} />
              {swipe.type}
              <ProfileAvatar userId={swipe.targetUserId} size={100} />
            </Stack>
            <Typography variant="caption">
              Status: {swipe.status}
            </Typography>
            <Typography variant="caption">
              Created: {timeAgo(swipe.createTime.toDate())}
            </Typography>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
};

const SwipeItemMemo = memo(SwipeItem, areEqual);

const Root = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  flex: 1,
});

interface Props {
  user: User;
}

const UserSwipes: React.FC<Props> = ({ user }) => {
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.userSwipes);
  const [ filters, setFilters ] = useQueryParams({
    who: withDefault(StringParam, 'all'),
  });


  useEffect(() => {
    dispatch(actions.loadSwipes(user.id));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ user.id ]);

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilters({
      [ event.target.name ]: event.target.value
    }, 'replaceIn');
  };

  if (state.loading || !state.swipes[ user.id ]) {
    return (
      <Root>
        <CircularProgress />
      </Root>
    );
  }

  let list: Swipe[] = [ ...state.swipes[ user.id ] ];

  if (filters.who === 'fromMe') {
    list = state.swipes[ user.id ].filter(item => item.userId === user.id);
  }
  else if (filters.who === 'toMe') {
    list = state.swipes[ user.id ].filter(item => item.targetUserId === user.id);
  }





  return (
    <Root>
      <Stack direction="column" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="who-label">From Who</InputLabel>
          <Select
            name="who"
            labelId="type-label"
            value={filters.who || 'all'}
            label="From Who"
            onChange={handleFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="fromMe">From me</MenuItem>
            <MenuItem value="toMe">To Me</MenuItem>
          </Select>
        </FormControl>

        <Divider flexItem />

        <ReactWindowScroller>
          {({ ref, outerRef, style, onScroll }) => (
            <FixedSizeList
              ref={ref}
              outerRef={outerRef}
              style={style}
              onScroll={onScroll}
              height={window.innerHeight}
              width="100%"
              itemCount={list.length}
              itemSize={170}
              itemData={list}
            >
              {SwipeItemMemo}
            </FixedSizeList>
          )}
        </ReactWindowScroller>

      </Stack>

    </Root>
  );
};

export default UserSwipes;